import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { skipToken } from '@reduxjs/toolkit/query'
import styled, { useTheme } from 'styled-components/native'

import { ProviderTaskType } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { InputField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/InputField'
import { XIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/XIcon'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { TertiaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tertiaryButton/TertiaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { InlineInfoBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineInfoBanner/InlineInfoBanner'
import { InlineWarningBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineWarningBanner/InlineWarningBanner'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { mixpanelEvents } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { usePatchProviderTaskMutation } from '../../../providers/dashboard/data/providerTasksApi'
import { useApproveTslLetterMutation, useGetTslLetterQuery, usePatchTslLetterMutation } from '../data/tslApi'
import { getTslDateRange } from '../utils/tslTaskUtils'

const Container = styled.View(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing['16px'],
}))

const Header = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
})

const MetadataRow = styled.View(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing['4px'],
}))

const ButtonRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: theme.spacing['16px'],
  marginTop: theme.spacing['8px'],
}))

const ProviderEditHighlight = styled(BodyText)(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  textDecorationLine: 'underline',
}))

const getProviderEditPlaceholders = (draft: string): string[] =>
  Array.from(new Set(Array.from(draft.matchAll(/\[[^\]\n]+\]/g), (match) => match[0])))

type TslReviewModalProps = {
  letterId: string | null
  onClose: () => void
}

export const TslReviewModal: React.FC<TslReviewModalProps> = ({ letterId, onClose }) => {
  const intl = useIntl()
  const { formatMessage } = intl
  const { colors } = useTheme()
  // currentData, not data: data keeps the previously opened letter while (or if) this one fails to load
  const { currentData: letter, isFetching, isError } = useGetTslLetterQuery(letterId ?? skipToken)
  const [patchLetter] = usePatchTslLetterMutation()
  const [approveLetter] = useApproveTslLetterMutation()
  const [patchTask, { isLoading: completing }] = usePatchProviderTaskMutation()
  const [draftText, setDraftText] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [approving, setApproving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setDraftText(null)
    setError(null)
  }, [letterId])

  const currentDraft = draftText ?? letter?.draftText ?? ''
  // Avoid flashing "Mark as done" while Approve & Share acknowledges the task
  const isCompleted = letter?.status === 'completed' && !approving
  const providerEditPlaceholders = isCompleted ? [] : getProviderEditPlaceholders(currentDraft)
  const hasProviderEdits = providerEditPlaceholders.length > 0
  const providerEditWarning = hasProviderEdits
    ? formatMessage({
        defaultMessage: 'Provider action required: replace the [BRACKETED TEXT] before approving.',
        description: 'TSL draft field warning when provider placeholders remain',
      })
    : undefined

  const handleSaveDraft = async () => {
    if (!letter) return
    setSaving(true)
    setError(null)
    try {
      await patchLetter({ id: letter.id, data: { draftText: currentDraft } }).unwrap()
      setDraftText(null)
    } catch {
      setError(
        formatMessage({
          defaultMessage: 'Failed to save the draft. Please try again.',
          description: 'Error message when saving a TSL draft fails',
        }),
      )
    } finally {
      setSaving(false)
    }
  }

  const handleApprove = async () => {
    if (!letter) return
    setApproving(true)
    setError(null)
    try {
      if (draftText !== null) {
        await patchLetter({ id: letter.id, data: { draftText: currentDraft } }).unwrap()
      }
      await approveLetter(letter.id).unwrap()
      track({ event: mixpanelEvents.TSL_APPROVED })
      if (letter.ppapiTaskId) {
        await handleMarkDone()
      } else {
        onClose()
      }
    } catch {
      setError(
        formatMessage({
          defaultMessage: 'Failed to approve the letter. Please try again.',
          description: 'Error message when approving a TSL fails',
        }),
      )
    } finally {
      setApproving(false)
    }
  }

  const handleMarkDone = async () => {
    if (!letter?.ppapiTaskId) return
    setError(null)
    try {
      await patchTask({
        id: letter.ppapiTaskId,
        kind: ProviderTaskType.TREATMENT_SUMMARY_LETTER,
        data: { status: 'acknowledged' },
      }).unwrap()
      onClose()
    } catch {
      setError(
        formatMessage({
          defaultMessage: 'Failed to mark the task as done. Please try again.',
          description: 'Error message when completing a TSL task fails',
        }),
      )
    }
  }

  const dateRange = getTslDateRange(intl, letter?.startDate, letter?.endDate)

  const contents = (
    <Container testID={tID('TslReviewModal-container')}>
      <Header>
        <Title
          size={TitleSize.MEDIUM}
          text={<FormattedMessage defaultMessage='Treatment Summary Letter' description='TSL modal title' />}
        />
        <PressableOpacity
          onPress={onClose}
          accessibilityLabel={formatMessage({
            defaultMessage: 'Close modal',
            description: 'Close TSL review modal',
          })}
          testID={tID('TslReviewModal-close')}
        >
          <XIcon size={24} fillColor={colors.iconDefault} />
        </PressableOpacity>
      </Header>

      {isFetching && !letter ? (
        <BodyText
          size={BodyTextSize.SMALL}
          text={<FormattedMessage defaultMessage='Loading...' description='Loading letter' />}
        />
      ) : letter ? (
        <>
          <MetadataRow>
            <BodyText
              size={BodyTextSize.SMALL}
              text={
                <FormattedMessage
                  defaultMessage='Dates: {dateRange}'
                  description='TSL date range'
                  values={{ dateRange }}
                />
              }
            />
            <BodyText
              size={BodyTextSize.SMALL}
              text={
                <FormattedMessage
                  defaultMessage='Sessions: {count}'
                  description='TSL session count'
                  values={{ count: letter.sessionCount }}
                />
              }
            />
          </MetadataRow>

          {hasProviderEdits && (
            <InlineWarningBanner
              title={
                <FormattedMessage
                  defaultMessage='You <u>must</u> edit the below information in the draft letter before approving:'
                  description='TSL draft warning title when provider placeholders remain'
                  values={{
                    u: (chunks: React.ReactNode[]) => <BodyText underline size={BodyTextSize.SMALL} text={chunks} />,
                  }}
                />
              }
              text={
                <>
                  {providerEditPlaceholders.map((placeholder) => (
                    <React.Fragment key={placeholder}>
                      {'\n'}
                      {'· '}
                      <ProviderEditHighlight
                        size={BodyTextSize.SMALL}
                        bold
                        color={colors.textWarning}
                        text={placeholder}
                        testID={tID('TslReviewModal-provider-edit')}
                      />
                    </React.Fragment>
                  ))}
                </>
              }
              testId='TslReviewModal-provider-edits'
              textSize={BodyTextSize.SMALL}
              backgroundColor={colors.backgroundWarning}
              iconColor={colors.iconWarning}
              textColor={colors.textPrimary}
              marginBottom='0px'
            />
          )}

          {!isCompleted && (
            <InlineInfoBanner
              text={
                <FormattedMessage
                  defaultMessage='Automated draft only captures <u>completed</u> sessions and notes. You are responsible for verifying all information before approving.'
                  description='TSL disclaimer that the provider must verify the letter is accurate'
                  values={{
                    u: (chunks: React.ReactNode[]) => <BodyText underline size={BodyTextSize.SMALL} text={chunks} />,
                  }}
                />
              }
              testId='TslReviewModal-accuracy-disclaimer'
              backgroundColor={colors.backgroundSection}
              textColor={colors.textSecondary}
              iconColor={colors.textSecondary}
              marginBottom='0px'
            />
          )}

          <InputField
            name='treatment-summary-letter-draft'
            label={formatMessage({
              defaultMessage: 'Draft letter',
              description: 'TSL draft text field label',
            })}
            value={currentDraft}
            onChange={(e) => setDraftText(e.nativeEvent.text)}
            multiline
            numberOfLines={12}
            readOnly={isCompleted}
            baseInputStyle={{ marginBottom: 0 }}
            error={providerEditWarning}
            showErrorAsWarning
            testID={tID('TslReviewModal-draft')}
          />

          {(error || isError) && (
            <BodyText
              size={BodyTextSize.SMALL}
              color={colors.textError}
              text={
                error ??
                formatMessage({
                  defaultMessage: 'Failed to refresh the letter. Please try again.',
                  description: 'Error message when refetching a TSL fails',
                })
              }
              testID={tID('TslReviewModal-error')}
            />
          )}

          {!isCompleted && (
            <ButtonRow>
              <TertiaryButton
                text={<FormattedMessage defaultMessage='Save Draft' description='Save draft button' />}
                onPress={handleSaveDraft}
                loading={saving}
                disabled={approving || draftText === null}
                testID={tID('TslReviewModal-save-draft')}
              />
              <PrimaryButton
                text={<FormattedMessage defaultMessage='Approve & Share' description='Approve letter button' />}
                onPress={handleApprove}
                loading={approving}
                disabled={saving || hasProviderEdits}
                testID={tID('TslReviewModal-approve')}
              />
            </ButtonRow>
          )}

          {isCompleted && (
            <>
              <BodyText
                size={BodyTextSize.SMALL}
                text={
                  <FormattedMessage defaultMessage='This letter has been approved.' description='Completed badge' />
                }
              />
              {letter.ppapiTaskId && (
                <ButtonRow>
                  <PrimaryButton
                    text={<FormattedMessage defaultMessage='Mark as done' description='Complete TSL task button' />}
                    onPress={handleMarkDone}
                    loading={completing}
                    testID={tID('TslReviewModal-mark-done')}
                  />
                </ButtonRow>
              )}
            </>
          )}
        </>
      ) : isError ? (
        <BodyText
          size={BodyTextSize.SMALL}
          color={colors.textError}
          text={
            <FormattedMessage
              defaultMessage='Failed to load the letter. Please try again.'
              description='Error message when loading a TSL fails'
            />
          }
          testID={tID('TslReviewModal-load-error')}
        />
      ) : null}
    </Container>
  )

  return (
    <Modal
      visible={letterId !== null}
      onRequestClose={onClose}
      disableBottomSheet
      enableTopOffset
      width='min(550px, calc(100vw - 32px))'
      showCloseIcon={false}
      modalContents={contents}
      accessibilityLabel={formatMessage({
        defaultMessage: 'Treatment Summary Letter review modal',
        description: 'Accessibility label for TSL review modal',
      })}
    />
  )
}
